/**
 * InjuryTicker — one game (or team) at a time: who is hurt, how badly, and a
 * larger card on hover/focus/tap.
 *
 * Cycle per entry: fade in → read pause → glide through the players if they
 * overflow → read pause (scales with player count) → fade out → next.
 * Everything pauses while the pointer is over the ticker or a card is open.
 * Reduced motion: instant swaps, no auto glide (the row scrolls by hand),
 * longer pauses.
 *
 * Entries are grouped by league (utils/injuryEntries), so the "‹ NBA" /
 * "NFL ›" labels are computed from the same sections the buttons jump to.
 * Each run of the cycle has its own cancel token: a skip used to flip one
 * shared flag back to false and leave two loops running.
 *
 * Group modes (settings):
 *   fixture — both teams' players together per matchup, grouped by team
 *   team    — one team per card (two cards per matchup)
 */

import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, Platform, Animated, Image, Easing, Pressable, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTicker } from '../context/TickerContext';
import { useReducedMotion, prefersReducedMotion } from '../utils/motion';
import { buildLeagueSections, adjacentSectionIndex, sectionIndexForItem } from '../utils/tickerNavigation';
import { describeInjuryStatus, injuryReason, severityRank } from '../utils/injuryStatus';
import { buildInjuryEntries, shortTeamName } from '../utils/injuryEntries';
import InjuryPopover from './InjuryPopover';

const IS_WEB = Platform.OS === 'web';
const TICKER_HEIGHT = 36;
const FONT = IS_WEB ? 'Slate, -apple-system, system-ui' : 'System';
const FADE_IN_MS = 260;
const FADE_OUT_MS = 200;
const GLIDE_SPEED = 42;          // px/s through the player list
const GAP_BETWEEN_ENTRIES = 220;
const HOVER_OPEN_MS = 90;
const HOVER_CLOSE_MS = 180;
const POPOVER_ID = 'injury-ticker-popover';
const FUNCTIONS_URL = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const pauseBefore = (reduced) => (reduced ? 3500 : 2200);
const pauseAfter = (count, reduced) => Math.max(2500, 800 + count * (reduced ? 1100 : 750));

/** Pausable sleep for one run: time only counts while not paused. Resolves false when cancelled. */
function pausableWait(ms, run, pausedRef) {
    return new Promise((resolve) => {
        let remaining = ms;
        let last = Date.now();
        run.wake = () => resolve(false);
        const tick = () => {
            if (run.cancelled) return resolve(false);
            const now = Date.now();
            if (!pausedRef.current) remaining -= now - last;
            last = now;
            if (remaining <= 0) return resolve(true);
            run.timer = setTimeout(tick, Math.min(120, remaining));
        };
        tick();
    });
}

function formatStart(ms) {
    if (!ms) return '';
    const d = new Date(ms);
    const now = new Date();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return time;
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    if (d.toDateString() === tomorrow.toDateString()) return `Tmrw ${time}`;
    return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
}

const leagueLabel = (l) => (l ? l.toUpperCase() : 'OTHER');

/** One player: headshot ringed in the status colour, name, status pill, reason. */
const PlayerChip = React.memo(function PlayerChip({ player, chipKey, active, onOpen, onHoverIn, onHoverOut, onToggle }) {
    const ref = useRef(null);
    const [imgFailed, setImgFailed] = useState(false);
    const status = describeInjuryStatus(player.injury_status);
    const reason = injuryReason(player);
    const rect = () => (ref.current && ref.current.getBoundingClientRect ? ref.current.getBoundingClientRect() : null);
    return (
        <Pressable
            ref={ref}
            dataSet={{ pressable: 'scale' }}
            role="button"
            aria-label={`${player.name}, ${status.long}${reason ? `, ${reason}` : ''}. Show details`}
            aria-describedby={active ? POPOVER_ID : undefined}
            aria-expanded={active}
            onHoverIn={() => onHoverIn(chipKey, player, rect)}
            onHoverOut={onHoverOut}
            onFocus={(e) => {
                // Keyboard focus opens the card; a mouse click (RNW focuses on mousedown) is handled by hover/press.
                const t = e?.target || e?.nativeEvent?.target;
                let keyboard = true;
                try { keyboard = !IS_WEB || !t?.matches || t.matches(':focus-visible'); } catch { keyboard = true; }
                if (keyboard) onOpen(chipKey, player, rect);
            }}
            onBlur={onHoverOut}
            onPress={() => onToggle(chipKey, player, rect)}
            style={({ hovered, focused }) => [s.chip, (hovered || active) && s.chipActive, focused && s.chipActive]}
        >
            <View style={[s.photoRing, { borderColor: status.border }]}>
                {player.headshot_url && !imgFailed ? (
                    <Image source={{ uri: player.headshot_url }} style={s.photo} onError={() => setImgFailed(true)} />
                ) : (
                    <View style={[s.photo, s.photoFallback]}>
                        <Text style={s.photoInitial}>{String(player.name || '?').charAt(0)}</Text>
                    </View>
                )}
            </View>
            <Text style={s.playerName} numberOfLines={1}>{player.name}</Text>
            <View style={[s.statusBadge, { backgroundColor: status.bg, borderColor: status.border }]}>
                <Text style={[s.statusText, { color: status.color }]}>{status.short}</Text>
            </View>
            {reason ? <Text style={s.reasonText} numberOfLines={1}>{reason}</Text> : null}
        </Pressable>
    );
});

const SkeletonRow = ({ reduced }) => {
    const pulse = useRef(new Animated.Value(0.5)).current;
    useEffect(() => {
        if (reduced) return undefined;
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(pulse, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.quad), useNativeDriver: !IS_WEB }),
            Animated.timing(pulse, { toValue: 0.5, duration: 700, easing: Easing.inOut(Easing.quad), useNativeDriver: !IS_WEB }),
        ]));
        loop.start();
        return () => loop.stop();
    }, [reduced, pulse]);
    return (
        <Animated.View style={[s.skeletonRow, { opacity: pulse }]} aria-busy="true" aria-label="Loading injury report">
            <View style={[s.skel, { width: 120 }]} />
            {[0, 1, 2].map((i) => (
                <View key={i} style={s.skelChip}>
                    <View style={s.skelDot} />
                    <View style={[s.skel, { width: 70 }]} />
                    <View style={[s.skel, { width: 26 }]} />
                </View>
            ))}
        </Animated.View>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const InjuryTicker = ({ isVisible = true }) => {
    const { fixtures, injuryTickerLeagues, injuryMinSeverity, injuryGroupBy } = useTicker();
    const reduced = useReducedMotion();

    const fade = useRef(new Animated.Value(0)).current;

    const [injuries, setInjuries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchFailed, setFetchFailed] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [compact, setCompact] = useState(false);

    // Player list scroll (auto glide drives it; the user can scroll it by hand while paused).
    const scrollRef = useRef(null);
    const scrollPosRef = useRef(0);
    const contentWidthRef = useRef(0);
    const viewportWidthRef = useRef(0);

    // Pause sources: pointer over the ticker, a card open, a finger down.
    const hoverRef = useRef(false);
    const touchRef = useRef(false);
    const touchTimer = useRef(null);
    useEffect(() => () => clearTimeout(touchTimer.current), []);
    const pausedRef = useRef(false);
    const [popover, setPopover] = useState(null); // { key, player, rect, entryIndex }
    const popoverRef = useRef(null);
    popoverRef.current = popover;
    const syncPaused = useCallback(() => {
        pausedRef.current = hoverRef.current || touchRef.current || !!popoverRef.current;
    }, []);
    useEffect(() => { syncPaused(); }, [popover, syncPaused]);

    // ─── Injury data ────────────────────────────────────────────────────────
    const fetchInjuries = useCallback(async () => {
        try {
            const res = await fetch(`${FUNCTIONS_URL}/getInjuryReport?league=all`, {
                method: 'GET', headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (data.success && Array.isArray(data.players)) {
                setInjuries(
                    data.players
                        .filter((p) => p.injury_status && p.injury_status !== 'active' && p.injury_status !== 'healthy')
                        .sort((a, b) => severityRank(a.injury_status) - severityRank(b.injury_status))
                );
                setFetchFailed(false);
            } else {
                throw new Error('Bad payload');
            }
        } catch (err) {
            console.warn('[InjuryTicker] Fetch error:', err.message);
            setFetchFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInjuries();
        const iv = setInterval(fetchInjuries, 15 * 60 * 1000);
        return () => clearInterval(iv);
    }, [fetchInjuries]);

    // ─── Entries + league sections ──────────────────────────────────────────
    const entries = useMemo(() => buildInjuryEntries({
        fixtures, injuries, leagues: injuryTickerLeagues, minSeverity: injuryMinSeverity, groupBy: injuryGroupBy,
    }), [fixtures, injuries, injuryTickerLeagues, injuryMinSeverity, injuryGroupBy]);
    const entriesKey = useMemo(() => entries.map((e) => e.id).join('|'), [entries]);
    const sections = useMemo(() => buildLeagueSections(entries, (e) => e.league), [entries]);
    const total = entries.length;

    const currentIdRef = useRef(null);
    const requestedIndexRef = useRef(null);
    const [skipSignal, setSkipSignal] = useState(0);

    const safeIndex = total > 0 ? Math.min(currentIndex, total - 1) : 0;
    const current = entries[safeIndex] || null;
    const sectionIdx = sectionIndexForItem(sections, safeIndex);
    const prevSection = sections[adjacentSectionIndex(sections, sectionIdx, 'prev')] || null;
    const nextSection = sections[adjacentSectionIndex(sections, sectionIdx, 'next')] || null;

    const closePopover = useCallback(() => setPopover(null), []);

    const skipToEntry = useCallback((idx) => {
        if (total === 0) return;
        const i = ((idx % total) + total) % total;
        requestedIndexRef.current = i;
        currentIdRef.current = entries[i]?.id ?? null;
        setCurrentIndex(i);
        setPopover(null);
        setSkipSignal((x) => x + 1);
    }, [total, entries]);

    const skipGame = (dir) => skipToEntry(safeIndex + (dir === 'next' ? 1 : -1));
    const skipLeague = (dir) => {
        const target = dir === 'next' ? nextSection : prevSection;
        if (target) skipToEntry(target.startIndex);
    };

    // ─── Cycle (one run per effect; each has its own cancel token) ──────────
    useEffect(() => {
        if (!isVisible || total === 0) {
            fade.setValue(0);
            return undefined;
        }
        const run = { cancelled: false, timer: null, raf: null, wake: null, anim: null };

        const animate = (toValue, duration) => new Promise((resolve) => {
            if (prefersReducedMotion() || duration === 0) { fade.setValue(toValue); resolve(); return; }
            run.anim = Animated.timing(fade, {
                toValue, duration,
                easing: toValue ? Easing.bezier(0.16, 1, 0.3, 1) : Easing.bezier(0.4, 0, 1, 1),
                useNativeDriver: !IS_WEB,
            });
            run.anim.start(() => resolve());
        });

        // Glides the player list to its end; pauses while paused; resolves false if cancelled.
        const glide = () => new Promise((resolve) => {
            let last = null;
            run.wake = () => resolve(false);
            const step = (t) => {
                if (run.cancelled) return resolve(false);
                const dt = last == null ? 0 : Math.min(0.1, (t - last) / 1000);
                last = t;
                const overflow = Math.max(0, contentWidthRef.current - viewportWidthRef.current);
                if (scrollPosRef.current >= overflow - 0.5) return resolve(true);
                if (!pausedRef.current) {
                    scrollPosRef.current = Math.min(overflow, scrollPosRef.current + GLIDE_SPEED * dt);
                    scrollRef.current?.scrollTo?.({ x: scrollPosRef.current, animated: false });
                }
                run.raf = requestAnimationFrame(step);
            };
            run.raf = requestAnimationFrame(step);
        });

        (async () => {
            let idx = requestedIndexRef.current;
            requestedIndexRef.current = null;
            if (idx == null) {
                const keep = entries.findIndex((e) => e.id === currentIdRef.current);
                idx = keep >= 0 ? keep : 0;
            }
            while (!run.cancelled) {
                idx = ((idx % total) + total) % total;
                currentIdRef.current = entries[idx].id;
                setCurrentIndex(idx);
                scrollPosRef.current = 0;
                scrollRef.current?.scrollTo?.({ x: 0, animated: false });

                fade.setValue(0);
                await animate(1, FADE_IN_MS);
                if (run.cancelled) return;
                if (!(await pausableWait(pauseBefore(prefersReducedMotion()), run, pausedRef))) return;
                if (!prefersReducedMotion()) {
                    if (!(await glide())) return;
                }
                const count = entries[idx].shownCount;
                if (!(await pausableWait(pauseAfter(count, prefersReducedMotion()), run, pausedRef))) return;
                // Never fade out from under an open card or the pointer.
                while (pausedRef.current && !run.cancelled) {
                    if (!(await pausableWait(150, run, { current: false }))) return;
                }
                await animate(0, FADE_OUT_MS);
                if (run.cancelled) return;
                if (!(await pausableWait(GAP_BETWEEN_ENTRIES, run, { current: false }))) return;
                idx += 1;
            }
        })();

        return () => {
            run.cancelled = true;
            if (run.timer) clearTimeout(run.timer);
            if (run.raf) cancelAnimationFrame(run.raf);
            if (run.anim) run.anim.stop();
            if (run.wake) run.wake();
        };
        // entries is covered by entriesKey; a new fixture/injury set restarts on the same entry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, total, skipSignal, entriesKey]);

    // ─── Popover open/close ─────────────────────────────────────────────────
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const clearTimers = () => {
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    };
    useEffect(() => () => clearTimers(), []);

    const openNow = useCallback((key, player, rectFn) => {
        clearTimers();
        const rect = rectFn();
        if (!rect) return;
        setPopover({ key, player, rect });
    }, []);
    const onChipHoverIn = useCallback((key, player, rectFn) => {
        clearTimers();
        if (popoverRef.current) { openNow(key, player, rectFn); return; } // already open: move instantly
        openTimer.current = setTimeout(() => openNow(key, player, rectFn), HOVER_OPEN_MS);
    }, [openNow]);
    const scheduleClose = useCallback(() => {
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setPopover(null), HOVER_CLOSE_MS);
    }, []);
    const keepOpen = useCallback(() => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    }, []);
    // Tap toggles (touch has no hover); a mouse click keeps the hovered card open.
    const onChipToggle = useCallback((key, player, rectFn) => {
        if (touchRef.current && popoverRef.current?.key === key) { clearTimers(); setPopover(null); return; }
        openNow(key, player, rectFn);
    }, [openNow]);

    // Escape, a tap outside, or the page scrolling closes the card.
    useEffect(() => {
        if (!IS_WEB || !popover || typeof document === 'undefined') return undefined;
        const onKey = (e) => { if (e.key === 'Escape') { clearTimers(); setPopover(null); } };
        const onDown = (e) => {
            const pop = document.getElementById(POPOVER_ID);
            if (pop && pop.contains(e.target)) return;
            if (e.target?.closest?.('[aria-describedby], [aria-expanded]')) return; // chips toggle themselves
            setPopover(null);
        };
        const onScroll = () => setPopover(null);
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [popover]);

    // The player row scrolls under the card when gliding resumes; close on entry change.
    useEffect(() => { setPopover(null); }, [safeIndex]);

    // ─── Render ─────────────────────────────────────────────────────────────
    if (!isVisible) return null;

    const hasMulti = total > 1;
    const leagueButtons = sections.length > 1 && !compact; // phones: game skips only, room for players
    const popContext = current
        ? `${current.away_team} @ ${current.home_team}${current.startMs ? ` · ${formatStart(current.startMs)}` : ''}`
        : '';

    let body;
    if (loading && injuries.length === 0) {
        body = <SkeletonRow reduced={reduced} />;
    } else if (!current) {
        const msg = fetchFailed && injuries.length === 0
            ? 'Injury report unavailable right now'
            : 'No injuries listed for upcoming games';
        body = (
            <View style={s.emptyRow}>
                <Ionicons name={fetchFailed && injuries.length === 0 ? 'cloud-offline-outline' : 'checkmark-circle-outline'} size={13} color="rgba(255,255,255,0.35)" />
                <Text style={s.emptyText}>{msg}</Text>
            </View>
        );
    } else {
        // Phones: the header rides in the scrolling row so players get the width.
        const header = (
            <>
                <View style={s.matchup}>
                    <View style={s.leaguePill}>
                        <Text style={s.leaguePillText}>{leagueLabel(current.league)}</Text>
                    </View>
                    {current.teamMode ? (
                        <>
                            <Text style={s.matchupText} numberOfLines={1}>{current.team}</Text>
                            <Text style={s.matchupVs} numberOfLines={1}>vs {compact ? shortTeamName(current.opponent) : current.opponent}</Text>
                        </>
                    ) : (
                        <Text style={s.matchupText} numberOfLines={1}>
                            {compact ? `${shortTeamName(current.away_team)} @ ${shortTeamName(current.home_team)}` : `${current.away_team} @ ${current.home_team}`}
                        </Text>
                    )}
                    {current.startMs ? <Text style={s.matchupTime}>{formatStart(current.startMs)}</Text> : null}
                </View>
                <View style={s.verticalDivider} />
            </>
        );
        body = (
            <Animated.View
                style={[s.slide, {
                    opacity: fade,
                    transform: [{ translateY: fade.interpolate({ inputRange: [0, 1], outputRange: [reduced ? 0 : 5, 0] }) }],
                }]}
            >
                {!compact && header}

                {/* ── Players ── */}
                <ScrollView
                    ref={scrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={s.playersScroll}
                    contentContainerStyle={s.playersContent}
                    scrollEventThrottle={32}
                    onScroll={(e) => { scrollPosRef.current = e.nativeEvent.contentOffset.x; }}
                    onLayout={(e) => { viewportWidthRef.current = e.nativeEvent.layout.width; }}
                    onContentSizeChange={(w) => { contentWidthRef.current = w; }}
                >
                    {compact && header}
                    {current.groups.map((group, gi) => (
                        <View key={group.team} style={s.teamGroup}>
                            {!current.teamMode ? (
                                <View style={[s.teamTag, gi > 0 && s.teamTagSpaced]}>
                                    <Text style={s.teamTagText} numberOfLines={1}>{shortTeamName(group.team).toUpperCase()}</Text>
                                </View>
                            ) : null}
                            {group.players.map((player) => {
                                const key = `${current.id}:${player.player_id || player.name}`;
                                return (
                                    <PlayerChip
                                        key={key}
                                        chipKey={key}
                                        player={player}
                                        active={popover?.key === key}
                                        onOpen={openNow}
                                        onHoverIn={onChipHoverIn}
                                        onHoverOut={scheduleClose}
                                        onToggle={onChipToggle}
                                    />
                                );
                            })}
                        </View>
                    ))}
                    {current.total > current.shownCount && (
                        <Text style={s.moreText}>+{current.total - current.shownCount} more</Text>
                    )}
                </ScrollView>
            </Animated.View>
        );
    }

    return (
        <View
            style={s.container}
            dataSet={{ injuryTicker: 'true' }}
            role="region"
            aria-label="Injury report"
            onLayout={(e) => setCompact(e.nativeEvent.layout.width < 640)}
            {...(IS_WEB ? {
                onMouseEnter: () => { hoverRef.current = true; syncPaused(); },
                onMouseLeave: () => { hoverRef.current = false; syncPaused(); },
            } : {})}
            onTouchStart={() => { clearTimeout(touchTimer.current); touchRef.current = true; syncPaused(); }}
            onTouchEnd={() => {
                clearTimeout(touchTimer.current);
                touchTimer.current = setTimeout(() => { touchRef.current = false; syncPaused(); }, 1500);
            }}
        >
            {/* ── Label ── */}
            <View style={s.label}>
                <Ionicons name="medkit" size={11} color="#f87171" />
                {!compact && <Text style={s.labelText}>INJURIES</Text>}
            </View>

            {/* ── Back: league (outer) | game (inner) ── */}
            {current && hasMulti && (
                <View style={s.leftControls}>
                    {leagueButtons && prevSection && (
                        <Pressable
                            onPress={() => skipLeague('prev')}
                            dataSet={{ pressable: 'scale' }}
                            role="button"
                            aria-label={`Back to ${leagueLabel(prevSection.league)} injuries`}
                            style={({ hovered }) => [s.leagueBtn, hovered && s.leagueBtnHover]}
                            hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                        >
                            <Ionicons name="play-skip-back" size={9} color="#c4b5fd" />
                            {!compact && <Text style={s.leagueBtnLabel}>{leagueLabel(prevSection.league)}</Text>}
                        </Pressable>
                    )}
                    <Pressable
                        onPress={() => skipGame('prev')}
                        dataSet={{ pressable: 'scale' }}
                        role="button"
                        aria-label="Previous game"
                        style={({ hovered }) => [s.gameBtn, hovered && s.gameBtnHover]}
                        hitSlop={{ top: 6, bottom: 6, left: 2, right: 2 }}
                    >
                        <Ionicons name="chevron-back" size={13} color="rgba(255,255,255,0.6)" />
                    </Pressable>
                </View>
            )}

            <View style={s.stage}>{body}</View>

            {/* ── Forward: game (inner) | league (outer) | counter ── */}
            {current && (
                <View style={s.rightControls}>
                    {hasMulti && (
                        <Pressable
                            onPress={() => skipGame('next')}
                            dataSet={{ pressable: 'scale' }}
                            role="button"
                            aria-label="Next game"
                            style={({ hovered }) => [s.gameBtn, hovered && s.gameBtnHover]}
                            hitSlop={{ top: 6, bottom: 6, left: 2, right: 2 }}
                        >
                            <Ionicons name="chevron-forward" size={13} color="rgba(255,255,255,0.6)" />
                        </Pressable>
                    )}
                    {hasMulti && leagueButtons && nextSection && (
                        <Pressable
                            onPress={() => skipLeague('next')}
                            dataSet={{ pressable: 'scale' }}
                            role="button"
                            aria-label={`Skip to ${leagueLabel(nextSection.league)} injuries`}
                            style={({ hovered }) => [s.leagueBtn, hovered && s.leagueBtnHover]}
                            hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                        >
                            {!compact && <Text style={s.leagueBtnLabel}>{leagueLabel(nextSection.league)}</Text>}
                            <Ionicons name="play-skip-forward" size={9} color="#c4b5fd" />
                        </Pressable>
                    )}
                    <View style={s.progress} aria-label={`${current.teamMode ? 'Team' : 'Game'} ${safeIndex + 1} of ${total}`}>
                        <Text style={s.progressText}>
                            <Text style={s.progressCurrent}>{safeIndex + 1}</Text>/{total}
                        </Text>
                    </View>
                </View>
            )}

            <InjuryPopover
                id={POPOVER_ID}
                open={!!popover}
                player={popover?.player}
                context={popContext}
                anchorRect={popover?.rect}
                onMouseEnter={() => { keepOpen(); hoverRef.current = true; syncPaused(); }}
                onMouseLeave={() => { hoverRef.current = false; syncPaused(); scheduleClose(); }}
            />
        </View>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
    container: {
        height: TICKER_HEIGHT,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(10,10,13,0.97)',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(248,113,113,0.12)',
        overflow: 'hidden',
    },
    label: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 10,
        height: '100%',
        backgroundColor: 'rgba(248,113,113,0.07)',
        borderRightWidth: 1,
        borderRightColor: 'rgba(248,113,113,0.14)',
    },
    labelText: {
        color: 'rgba(248,113,113,0.9)',
        fontSize: 9,
        fontWeight: '800',
        letterSpacing: 1,
        fontFamily: FONT,
    },
    stage: { flex: 1, height: '100%', minWidth: 0, overflow: 'hidden' },
    slide: { flexDirection: 'row', alignItems: 'center', height: '100%' },

    // ── Controls ─────────────────────────────────────────────────────────
    leftControls: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        paddingHorizontal: 4,
        gap: 2,
        borderRightWidth: 1,
        borderRightColor: 'rgba(255,255,255,0.06)',
    },
    rightControls: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        paddingLeft: 4,
        gap: 2,
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.06)',
    },
    gameBtn: {
        width: 24,
        height: 24,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    gameBtnHover: { backgroundColor: 'rgba(255,255,255,0.08)' },
    leagueBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        height: 22,
        paddingHorizontal: 7,
        borderRadius: 6,
        backgroundColor: 'rgba(139,92,246,0.14)',
        borderWidth: 1,
        borderColor: 'rgba(139,92,246,0.28)',
    },
    leagueBtnHover: { backgroundColor: 'rgba(139,92,246,0.28)', borderColor: 'rgba(167,139,250,0.55)' },
    leagueBtnLabel: {
        color: '#c4b5fd',
        fontSize: 9,
        fontWeight: '700',
        letterSpacing: 0.5,
        fontFamily: FONT,
    },

    // ── Header ───────────────────────────────────────────────────────────
    matchup: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingLeft: 10,
        paddingRight: 6,
        flexShrink: 0,
    },
    leaguePill: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.07)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    leaguePillText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 8.5,
        fontWeight: '800',
        letterSpacing: 0.6,
        fontFamily: FONT,
    },
    matchupText: {
        color: '#fff',
        fontSize: 11.5,
        fontWeight: '700',
        fontFamily: FONT,
        maxWidth: 230,
    },
    matchupVs: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 10.5,
        fontWeight: '500',
        fontFamily: FONT,
        maxWidth: 140,
    },
    matchupTime: {
        color: 'rgba(255,255,255,0.38)',
        fontSize: 10,
        fontWeight: '600',
        fontFamily: FONT,
        fontVariant: ['tabular-nums'],
    },
    verticalDivider: {
        width: 1,
        height: 16,
        backgroundColor: 'rgba(255,255,255,0.08)',
        marginHorizontal: 4,
    },

    // ── Players ──────────────────────────────────────────────────────────
    playersScroll: { flex: 1, height: '100%' },
    playersContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingRight: 16,
        paddingLeft: 2,
        height: '100%',
    },
    teamGroup: { flexDirection: 'row', alignItems: 'center', gap: 2 },
    teamTag: {
        paddingHorizontal: 5,
        paddingVertical: 1,
        marginRight: 2,
    },
    teamTagSpaced: {
        marginLeft: 8,
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.1)',
        paddingLeft: 10,
    },
    teamTagText: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 8.5,
        fontWeight: '800',
        letterSpacing: 0.8,
        fontFamily: FONT,
        maxWidth: 90,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        height: 26,
        paddingLeft: 3,
        paddingRight: 8,
        borderRadius: 13,
        ...(IS_WEB ? { cursor: 'pointer', transitionProperty: 'background-color', transitionDuration: '140ms' } : {}),
    },
    chipActive: { backgroundColor: 'rgba(255,255,255,0.07)' },
    photoRing: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 1.5,
        padding: 1,
    },
    photo: { width: '100%', height: '100%', borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.06)' },
    photoFallback: { alignItems: 'center', justifyContent: 'center' },
    photoInitial: { color: 'rgba(255,255,255,0.6)', fontSize: 9, fontWeight: '700', fontFamily: FONT },
    playerName: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 11.5,
        fontWeight: '600',
        fontFamily: FONT,
        maxWidth: 130,
    },
    statusBadge: {
        paddingHorizontal: 5,
        paddingVertical: 1.5,
        borderRadius: 4,
        borderWidth: 1,
    },
    statusText: {
        fontSize: 8.5,
        fontWeight: '800',
        letterSpacing: 0.4,
        fontFamily: FONT,
    },
    reasonText: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 10,
        fontWeight: '500',
        maxWidth: 110,
        fontFamily: FONT,
    },
    moreText: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 10,
        fontWeight: '600',
        marginLeft: 8,
        fontFamily: FONT,
    },

    // ── Counter ──────────────────────────────────────────────────────────
    progress: {
        paddingHorizontal: 8,
        height: '100%',
        justifyContent: 'center',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.05)',
        marginLeft: 2,
    },
    progressText: {
        color: 'rgba(255,255,255,0.28)',
        fontSize: 9.5,
        fontWeight: '700',
        fontFamily: FONT,
        fontVariant: ['tabular-nums'],
    },
    progressCurrent: { color: 'rgba(255,255,255,0.7)' },

    // ── Loading / empty (same height as the live row: nothing shifts) ───
    skeletonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        gap: 14,
        paddingLeft: 12,
    },
    skel: { height: 9, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)' },
    skelChip: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    skelDot: { width: 20, height: 20, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.08)' },
    emptyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        gap: 7,
        paddingLeft: 12,
    },
    emptyText: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 11,
        fontWeight: '500',
        fontFamily: FONT,
    },
});

export default InjuryTicker;
